/* eslint-disable no-param-reassign */

import React, { useState, useEffect } from 'react';

import styles from './FilterBar.module.scss';
import SourceIcon from '../SourceIcon';
import FilterPanel from '../../common/FilterPanel';
import FlatFilterSection from '../../common/FlatFilterSection';
import UnitFilterSection from '../../common/UnitFilterSection';
import {
  AssessmentFilter,
  AssessmentFilterField,
  AssessmentUnitFilter,
} from '../types';
import { userInfo } from '../../actions';

interface Props {
  filters: AssessmentFilter;
  onFilterChange: (
    { newFilters, unitValFromFilterBar }: { newFilters: AssessmentFilter, unitValFromFilterBar?: number }
  ) => void;
  openedUnitValue?: number;
}

const sectionTitle = (title: string) => (
  <div className={styles.categoryTitle}>{title}</div>
);

const FilterBar = ({ filters, onFilterChange, openedUnitValue = undefined }: Props) => {
  const [openUnitVal, setOpenUnitVal] = useState(-1);
  const isStudent = !userInfo.staffer;

  useEffect(() => {
    setOpenUnitVal(openedUnitValue || -1);
  }, [openedUnitValue]);

  const handleLessonChange = (value: string | number, checked: boolean) => {
    let unitValToKeep;

    const newFilters = { ...filters };
    newFilters.unit?.forEach((unitFilter) => {
      const lesson = unitFilter.lessons.find(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        lesson => lesson.value === value,
      );

      if (lesson) {
        lesson.checked = checked;
        unitValToKeep = unitFilter.value;
      }
    });

    newFilters.lesson?.forEach((lessonFilter) => {
      if (lessonFilter.value === value) {
        lessonFilter.checked = checked;
      }
    });

    onFilterChange({ newFilters, unitValFromFilterBar: unitValToKeep });
  };

  const handleUnitChange = (
    oldUnit: AssessmentUnitFilter,
    checked: boolean,
  ) => {
    const newFilters = { ...filters };
    const newLessons = oldUnit.lessons.map(lesson => ({ ...lesson, checked }));

    newFilters.unit?.forEach((unitFilter) => {
      if (unitFilter.label === oldUnit.label) {
        unitFilter.lessons = newLessons;
      }
    });

    onFilterChange({ newFilters, unitValFromFilterBar: oldUnit.value });
  };

  const handleCategoryChange = (value: string | number, checked: boolean) => {
    const newFilters = { ...filters };
    const newCategory = newFilters.category.find(
      categoryFilter => categoryFilter.value === value,
    );
    if (newCategory) {
      newCategory.checked = checked;
    }
    newFilters.page = 1;
    onFilterChange({ newFilters });
  };

  const handleSourceChange = (value: string | number, checked: boolean) => {
    const newFilters = { ...filters };

    const newIncluded = newFilters.source.find(
      sourceFilter => sourceFilter.value === value,
    );
    if (newIncluded) {
      newIncluded.checked = checked;
    }

    newFilters.page = 1;

    onFilterChange({ newFilters });
  };

  const handleFilterClear = () => {
    const newFilters = { ...filters };
    const setUnchecked = (filter: AssessmentFilterField) => {
      filter.checked = false;
    };

    newFilters.unit?.forEach((unitFilter) => {
      unitFilter.lessons.forEach(setUnchecked);
    });
    newFilters.lesson?.forEach(setUnchecked);
    newFilters.category.forEach(setUnchecked);
    newFilters.source.forEach(setUnchecked);

    onFilterChange({ newFilters });
  };

  // AIDEV-NOTE: AssessmentLibrary keeps the unit row expanded if it's the
  // currently-opened unit OR any of its lessons are checked. Games drops
  // this — its UnitFilterSection uses the default lessons-checked logic.
  const shouldCollapseUnit = (unitFilter: AssessmentUnitFilter) => (
    openUnitVal !== unitFilter.value && unitFilter.lessons.every(l => !l.checked)
  );

  return (
    <FilterPanel onClear={handleFilterClear}>
      {!!filters.unit?.length && (
        <UnitFilterSection
          shouldCollapseUnit={shouldCollapseUnit}
          title={sectionTitle('Unit')}
          units={filters.unit}
          onLessonChange={handleLessonChange}
          onUnitChange={handleUnitChange}
        />
      )}

      {!!filters.lesson?.length && (
        <FlatFilterSection
          filters={filters.lesson}
          title={sectionTitle('Lesson')}
          onChange={handleLessonChange}
        />
      )}

      {!!filters.category.length && (
        <FlatFilterSection
          filters={filters.category}
          title={sectionTitle('Category')}
          onChange={handleCategoryChange}
        />
      )}

      {filters.source.length > 0 && !isStudent && (
        <FlatFilterSection
          filters={filters.source}
          renderLabel={sourceFilter => (
            <div className={styles.sourceFilter}>
              <span className={styles.sourceFilterLabel}>{sourceFilter.label}</span>
              <SourceIcon
                firstName={userInfo.staffer?.first_name}
                lastName={userInfo.staffer?.last_name}
                size={18}
                source={sourceFilter.label}
              />
            </div>
          )}
          title={sectionTitle('Source')}
          onChange={handleSourceChange}
        />
      )}
    </FilterPanel>
  );
};

export default FilterBar;
